import { Grid, IconButton, Typography, OutlinedInput, InputAdornment, AppBar, Toolbar, Tooltip, Box, Button } from '@mui/material';
import { Close, Send } from '@material-ui/icons';
import JsonIcon from '../../assets/icons/json-icon.png';
import PdfIcon from '../../assets/icons/pdf-icon.png';
import { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { Download, OpenInNew, ZoomOutMap } from '@mui/icons-material';
import { baixarArquivo } from '../../utils/baixarArquivo';

const fileIcons = {
  json: JsonIcon,
  pdf: PdfIcon,
};

const FilePreview = ({ selectedFile, setSelectedFile, handleSendMessage, newMessage, setNewMessage, isChat }) => {
  const { theme } = useContext(ThemeContext);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const [textContent, setTextContent] = useState('');

  // Zoom da imagem com o scroll do mouse. Quando ampliada, arrastar move a
  // imagem. Feito na mao com transform para nao adicionar dependencia.
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 6;
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragOriginRef = useRef(null);
  const imageWrapRef = useRef(null);

  useEffect(() => {
    setPdfLoadError(false);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [selectedFile]);

  const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

  // O listener do wheel precisa ser nao-passivo para o preventDefault valer e a
  // pagina atras nao rolar junto; React registra onWheel como passivo.
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setZoom((prev) => {
      const next = clampZoom(prev * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
      if (next === 1) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  useEffect(() => {
    const el = imageWrapRef.current;
    if (!el) return undefined;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel, selectedFile, zoom]);

  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    e.preventDefault();
    dragOriginRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
    setDragging(true);
  };

  // Move/solta ficam na janela, nao na caixa: preso so na caixa, tirar o mouse
  // de cima (justamente o que acontece ao arrastar uma imagem ampliada) cancelava
  // o arraste no meio.
  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      const o = dragOriginRef.current;
      if (!o) return;
      setOffset({ x: e.clientX - o.x, y: e.clientY - o.y });
    };
    const onUp = () => { dragOriginRef.current = null; setDragging(false); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  const resetZoom = () => { setZoom(1); setOffset({ x: 0, y: 0 }); };

  const isTxt = (file) => {
    if (typeof file === 'string') return file.match(/\.txt$/i);
    return file.type === 'text/plain' || file.name?.match(/\.txt$/i);
  };

  useEffect(() => {
    if (!selectedFile || !isTxt(selectedFile)) {
      setTextContent('');
      return;
    }
    if (typeof selectedFile === 'string') {
      fetch(selectedFile)
        .then((r) => r.text())
        .then(setTextContent)
        .catch(() => setTextContent(''));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setTextContent(reader.result ?? '');
    reader.onerror = () => setTextContent('');
    reader.readAsText(selectedFile, 'UTF-8');
  }, [selectedFile]);

  const getFileIcon = (file) => {
    const extension = typeof file === 'string' ? file.split('.').pop().toLowerCase() : file.name.split('.').pop().toLowerCase();
    return fileIcons[extension] || PdfIcon;
  };

  const isImage = (file) => {
    if (typeof file === 'string') {
      return file.startsWith('data:image') || file.match(/\.(jpeg|jpg|gif|png|webp)$/i);
    }
    return file.type?.startsWith('image/');
  };

  const isPdf = (file) => {
    if (typeof file === 'string') {
      return file.match(/\.pdf$/i) || file.includes('pdf');
    }
    return file.type === 'application/pdf' || file.name?.match(/\.pdf$/i);
  };

  // PDF precisa de mais area que imagem: a 66% de largura uma pagina A4 fica
  // pequena demais para leitura. Calculado uma vez e usado no container.
  const showingPdf = selectedFile ? isPdf(selectedFile) : false;

  /*
   * O DOWNLOAD NÃO BAIXAVA (Gisele, 23/10/2026).
   *
   * O botão está aqui desde sempre, mas o anexo vem de
   * `api-alodesk.sysfar.com.br` e a tela roda em `alodesk.sysfar.com.br`: o
   * atributo `download` de um link é IGNORADO quando a origem é outra. Com o
   * `target="_blank"` que havia aqui, o clique abria a imagem numa aba nova --
   * e quem usa descreve isso como "só dá para ampliar".
   *
   * Quem resolve mora em `utils/baixarArquivo`, com teste: busca o conteúdo,
   * salva um endereço `blob:` (que é da própria página, e aí o `download`
   * vale) e tira o nome do parâmetro `path` -- porque o fim da URL do anexo é
   * a palavra `attachment`, não o nome do arquivo.
   */
  const downloadFile = () => { baixarArquivo(selectedFile); };


  return (
    <Grid
      container
      sx={{
        padding: '1rem',
        backgroundColor: theme.palette.background.modal,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: isChat ? '80%' : (showingPdf ? '92%' : '66%'),
        maxWidth: showingPdf ? '1400px' : '1200px',
        height: showingPdf ? '92vh' : '85vh',
        maxHeight: '95vh',
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
        justifyContent: 'space-between',
        boxShadow: theme.shadows[24],
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {/* Barra Superior com Botões */}
      <AppBar position="static" color="transparent" elevation={0} sx={{ width: '100%', borderRadius: '8px 8px 0 0' }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Grid container flexWrap={'nowrap'} flexDirection={'row'} sx={{ bgcolor: 'transparent' }}>
            <Grid container item sx={{ bgcolor: "transparent" }}>
              {/* Botão para remover o arquivo */}
              <Tooltip title={"Fechar"}>
                <IconButton onClick={() => setSelectedFile(null)} size="small">
                  <Close />
                </IconButton>
              </Tooltip>
              <Typography variant="h6">Pré-visualização</Typography>
            </Grid>
            {isImage(selectedFile) && (
              <Grid item sx={{ bgcolor: 'transparent', display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                  {zoom > 1 ? `${Math.round(zoom * 100)}%` : 'Role para ampliar'}
                </Typography>
                {zoom > 1 && (
                  <Tooltip title="Restaurar zoom (duplo clique na imagem)">
                    <IconButton onClick={resetZoom} size="small">
                      <ZoomOutMap fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
            )}
            {!handleSendMessage && (
              <Grid item sx={{ bgcolor: "transparent" }}>
                {/* Botão para download */}
                <Tooltip title={"Download"}>
                  <IconButton onClick={downloadFile} size="small">
                    <Download />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Toolbar>
      </AppBar>
      <Grid
        item
        sx={{
          bgcolor: 'transparent',
          minWidth: 0,
          width: '100%',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          // Imagem: o wrapper de zoom precisa esticar e cuidar do proprio recorte.
          // Demais tipos mantem o comportamento antigo (centralizado, com rolagem).
          alignItems: isImage(selectedFile) ? 'stretch' : 'center',
          justifyContent: 'center',
          overflow: isImage(selectedFile) ? 'hidden' : 'auto',
        }}
      >
        {isImage(selectedFile) ? (
          <Box
            ref={imageWrapRef}
            onMouseDown={handleMouseDown}
            onDoubleClick={resetZoom}
            sx={{
              // alignSelf stretch vence o alignItems:center do container pai —
              // sem isso o wrapper nao ocupa a area e a imagem encolhe ao ampliar.
              flex: 1,
              alignSelf: 'stretch',
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
            }}
          >
            <img
              src={typeof selectedFile === 'string' ? selectedFile : URL.createObjectURL(selectedFile)}
              alt="Pré-visualização"
              draggable={false}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '8px',
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                transition: dragging ? 'none' : 'transform 0.08s ease-out',
                userSelect: 'none',
                willChange: 'transform',
              }}
            />
          </Box>
        ) : isTxt(selectedFile) ? (
          <Box
            sx={{
              width: '100%',
              maxHeight: isChat ? '35rem' : 'calc(90vh - 120px)',
              overflow: 'auto',
              p: 2,
              bgcolor: theme.palette.background.paper,
              borderRadius: '8px',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: theme.palette.text.primary,
              }}
            >
              {textContent || 'Carregando...'}
            </Typography>
          </Box>
        ) : isPdf(selectedFile) ? (
          pdfLoadError ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: isChat ? '35rem' : 'calc(90vh - 120px)',
              gap: 2,
              padding: 2
            }}>
              <img 
                src={PdfIcon} 
                alt="PDF" 
                style={{ width: '64px', height: '64px', opacity: 0.7 }}
              />
              <Typography variant="h6" color="text.secondary">
                Não foi possível carregar o PDF
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                O arquivo pode estar protegido ou ter restrições de acesso
              </Typography>
              <Button
                variant="contained"
                startIcon={<OpenInNew />}
                onClick={() => {
                  const fileUrl = typeof selectedFile === 'string' ? selectedFile : URL.createObjectURL(selectedFile);
                  window.open(fileUrl, '_blank');
                }}
                sx={{ mt: 1 }}
              >
                Abrir em Nova Aba
              </Button>
            </Box>
          ) : (
            <iframe
              src={typeof selectedFile === 'string' ? selectedFile : URL.createObjectURL(selectedFile)}
              style={{
                width: '100%',
                minWidth: 0,
                height: '100%',
                alignSelf: 'stretch',
                flex: 1,
                border: 'none',
                borderRadius: '8px',
              }}
              title="Pré-visualização do PDF"
              onError={(e) => {
                console.warn('Erro ao carregar PDF no iframe:', e);
                setPdfLoadError(true);
              }}
              onLoad={() => {
                setPdfLoadError(false);
              }}
            />
          )
        ) : (
          <img
            src={getFileIcon(selectedFile)}
            alt="Ícone do Arquivo"
            style={{ width: '50px', height: '50px' }}
          />
        )}
      </Grid>
      {/* Visualização do arquivo */}
      <Grid container flexDirection={'column'} alignContent={'center'} alignItems={'center'} justifyContent={'center'} item
        sx={{ bgcolor: 'transparent' }}>


        {/* Caixa de texto para mensagem abaixo da imagem */}
        {handleSendMessage && (
          <Grid item sx={{ width: '100%', marginTop: '1rem', padding: '0 1rem', bgcolor: 'transparent' }}>
            <OutlinedInput
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              fullWidth
              placeholder="Digite uma mensagem para enviar com o arquivo"
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={handleSendMessage} size="small" color="primary">
                    <Send />
                  </IconButton>
                </InputAdornment>
              }
              sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: '8px',
                '& .MuiInputBase-input': {
                  padding: '8px 12px',
                },
              }}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default FilePreview;
